import React from 'react';
import { Button, Switch } from 'antd';
import { Drawer, EmptyState } from '../../../common/ui';
import {
  countInterventions,
  formatScenarioActions,
  fromApiInterventions,
} from '../model/interventions';
import { computeScenarioKPIs, estimateCapacityVph, getFirstName, vcColor } from '../model/metrics';

const STATUS_LABELS = {
  simulated: 'Simulato',
  pending: 'In attesa',
  base: 'Base',
};

const DELTA_LABELS = {
  total_flow_on_edges: 'Flusso',
  mean_vc_loaded: 'V/C medio',
  max_vc: 'V/C max',
  critical_edges: 'Archi critici',
  saturated_edges: 'Archi saturi',
  total_travel_time: 'Tempo tot.',
  total_delay: 'Ritardo',
  mean_speed: 'Velocità',
  unassigned_demand: 'Dom. non assegnata',
};

export function AssignxKpiBar({ flow, meanVc, closed, mods, status, hideClosed = false }) {
  const flowLabel = Number.isFinite(Number(flow)) ? Number(flow).toLocaleString('it-IT') : (flow ?? '—');
  return (
    <div className="assignx-kpi-bar">
      <div className="assignx-kpi-item"><b>{flowLabel}</b><span>Flusso rete v/h</span></div>
      <div className="assignx-kpi-item"><b>{meanVc ?? '—'}</b><span>V/C medio</span></div>
      {!hideClosed && <div className="assignx-kpi-item warn"><b>{closed ?? 0}</b><span>Strade chiuse</span></div>}
      <div className="assignx-kpi-item"><b>{mods ?? 0}</b><span>Interventi attivi</span></div>
      <div className="assignx-kpi-item assignx-kpi-status">
        <b><span className={`assignx-status-dot ${status}`} />{STATUS_LABELS[status] || STATUS_LABELS.base}</b>
        <span>Stato scenario</span>
      </div>
    </div>
  );
}

const fmtVc = (value) => (Number(value) || 0).toFixed(2).replace('.', ',');

const ACTION_KINDS = [
  [/chiusura/i, 'closure'],
  [/senso di marcia/i, 'reverse'],
  [/capacit/i, 'capacity'],
  [/nuova strada/i, 'new'],
];

function actionKind(action) {
  const match = ACTION_KINDS.find(([pattern]) => pattern.test(action));
  return match ? match[1] : 'other';
}

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

function formatSavedAt(scenario) {
  const raw = scenario?.updated_at || scenario?.created_at;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function ScenarioActionList({ actions, limit }) {
  if (!actions.length) return null;
  const visible = limit ? actions.slice(0, limit) : actions;
  const hidden = actions.length - visible.length;
  return (
    <ol className="assignx-action-list">
      {visible.map((action, index) => (
        <li key={`${action}-${index}`} className={`assignx-action ${actionKind(action)}`}>
          Intervento {index + 1}: {action}
        </li>
      ))}
      {hidden > 0 && <li className="assignx-action-more">{plural(hidden, 'altro intervento', 'altri interventi')}</li>}
    </ol>
  );
}

export function InterventionsDrawer({
  open,
  interventions,
  newRoads,
  edgeMeta,
  featuresByUid,
  comparisonSides,
  onClose,
  onReset,
  onSave,
  scenarios = [],
  currentScenarioName,
  isDirty = true,
  onLoad,
  onDelete,
  onCompare,
  activeScenarioId = null,
  busy = false,
}) {
  if (!open) return null;
  const currentActions = formatScenarioActions(interventions, newRoads, edgeMeta, featuresByUid);
  const compareCount = (comparisonSides || []).reduce(
    (total, side) => total + countInterventions(side.interventions, side.newRoads, edgeMeta),
    0,
  );
  return (
    <Drawer
      open={open}
      title="Interventi e Scenari"
      subtitle={
        comparisonSides
          ? `${compareCount} modifiche negli scenari confrontati`
          : 'Configurazione corrente e scenari salvati'
      }
      onClose={onClose}
      busy={busy}
      className="assignx-drawer assignx-int-drawer"
    >
        {comparisonSides ? comparisonSides.map((side) => {
          const sideActions = formatScenarioActions(side.interventions, side.newRoads, edgeMeta, featuresByUid);
          return (
            <section className="assignx-compare-interventions" key={`${side.key}-${side.id}`}>
              <h4><span>{side.position}</span>{side.name}</h4>
              {sideActions.length
                ? <ScenarioActionList actions={sideActions} />
                : <p className="assignx-scenario-hint">Nessun intervento in questo scenario.</p>}
            </section>
          );
        }) : (
          <>
            <section className="assignx-scenario-summary current">
              <header className="assignx-scenario-head">
                <h4>
                  {currentScenarioName}
                  {isDirty && <span className="assignx-dirty-flag">* non ancora salvato</span>}
                </h4>
                <p className="assignx-scenario-meta">
                  {currentActions.length
                    ? plural(currentActions.length, 'intervento configurato', 'interventi configurati')
                    : 'Scenario di partenza, nessuna modifica'}
                </p>
              </header>
              {currentActions.length ? (
                <ScenarioActionList actions={currentActions} />
              ) : (
                <p className="assignx-scenario-hint">
                  Seleziona una strada sulla mappa per chiuderla, invertirne il senso o modificarne la capacità.
                </p>
              )}
              <div className="assignx-drawer-actions">
                <Button size="small" danger disabled={busy || !currentActions.length} onClick={onReset}>Azzera tutto</Button>
                <Button size="small" type="primary" disabled={busy || !isDirty || !currentActions.length} onClick={onSave}>Salva come scenario</Button>
              </div>
            </section>

            <section className="assignx-saved-scenarios">
              <header className="assignx-section-head">
                <h3>Scenari salvati</h3>
                {!!scenarios.length && <span className="assignx-section-count">{scenarios.length}</span>}
              </header>
              {!scenarios.length && <EmptyState>Nessuno scenario salvato</EmptyState>}
              {scenarios.map((scenario) => {
                const actions = formatScenarioActions(
                  fromApiInterventions(scenario.definition),
                  scenario.definition?.new_roads || [],
                  edgeMeta,
                  featuresByUid,
                );
                const isActive = activeScenarioId != null && String(activeScenarioId) === String(scenario.id);
                const savedAt = formatSavedAt(scenario);
                return (
                  <article key={scenario.id} className={`assignx-scenario-summary saved${isActive ? ' active' : ''}`}>
                    <button
                      type="button"
                      className="assignx-scenario-load"
                      disabled={busy}
                      title="Carica questo scenario sulla mappa"
                      onClick={() => onLoad?.(scenario)}
                    >
                      <span className="assignx-scenario-head">
                        <span className="assignx-scenario-name">{scenario.name}</span>
                        <span className="assignx-scenario-meta">
                          {plural(actions.length, 'intervento', 'interventi')}
                          {savedAt ? ` · ${savedAt}` : ''}
                        </span>
                      </span>
                      <span className="assignx-scenario-load-cue">{isActive ? 'Caricato' : 'Carica'}</span>
                    </button>
                    <ScenarioActionList actions={actions} limit={4} />
                    <div className="assignx-scenario-actions">
                      <Button size="small" type="text" disabled={busy} onClick={() => onCompare?.(scenario)}>Confronta</Button>
                      <Button size="small" type="text" danger disabled={busy} onClick={() => onDelete?.(scenario)}>Elimina</Button>
                    </div>
                  </article>
                );
              })}
            </section>
          </>
        )}
    </Drawer>
  );
}

export function NetworkStatsDrawer({ open, features, interventions, newRoads, onClose, onOpenLink }) {
  if (!open) return null;
  const analysis = computeScenarioKPIs(features, interventions, newRoads);
  const lines = (features || []).filter((feature) => feature.geometry?.type === 'LineString');
  const totalKm = lines.reduce((sum, feature) => sum + Number(feature.properties?.length || 0) / 1000, 0);
  const averageSpeed = lines.length ? lines.reduce((sum, feature) => sum + Number(feature.properties?.speed_kph || 0), 0) / lines.length : 0;
  const critical = lines.map((feature) => {
    const p = feature.properties || {};
    return { feature, vc: Number(p.flow || 0) / Math.max(1, Number(p.capacity_vph || 600)) };
  }).sort((a, b) => b.vc - a.vc).slice(0, 10);
  const buckets = [0, 0, 0, 0];
  critical.concat(lines.slice(10).map((feature) => {
    const p = feature.properties || {};
    return { vc: Number(p.flow || 0) / Math.max(1, Number(p.capacity_vph || 600)) };
  })).forEach(({ vc }) => { buckets[vc < .5 ? 0 : vc < 1 ? 1 : vc < 1.5 ? 2 : 3] += 1; });
  const maxBucket = Math.max(...buckets, 1);
  return (
    <Drawer
      open={open}
      title="Statistiche di rete"
      subtitle="Calcolate sullo scenario corrente"
      onClose={onClose}
      className="assignx-drawer assignx-stats-drawer"
    >
        <div className="assignx-stat-grid">
          <div><b>{lines.length}</b><span>Archi totali</span></div><div><b>{totalKm.toFixed(0)} km</b><span>Km rete censiti</span></div>
          <div><b>{Math.round(averageSpeed)}</b><span>Velocità media km/h</span></div><div><b>{analysis.scenario.critical_edges}</b><span>Archi critici</span></div>
        </div>
        <h4>Distribuzione V/C</h4>
        <div className="assignx-histogram">{buckets.map((value, index) => <div key={index} title={`${value} archi`} style={{ height: Math.max(4, value / maxBucket * 80) }} />)}</div>
        <div className="assignx-hist-labels"><span>&lt; 0,5</span><span>0,5–1</span><span>1–1,5</span><span>&gt; 1,5</span></div>
        <h4>Top 10 criticità</h4>
        {critical.map(({ feature, vc }, index) => <button className="assignx-critical-row" key={feature.properties?.edge_uid} onClick={() => onOpenLink(feature.properties.edge_uid, feature)}><span>{index + 1}</span><b>{getFirstName(feature.properties?.name)}</b><strong style={{ color: vcColor(vc) }}>{fmtVc(vc)}</strong></button>)}
    </Drawer>
  );
}

export function NewRoadPanel({ open, draft, setDraft, onCancel, onConfirm, busy = false }) {
  if (!open) return null;
  return (
    <div className="assignx-newroad-panel">
      <h3>Caratteristiche nuova strada</h3>
      <div className="assignx-newroad-sub">
        <span className="assignx-status-dot simulated" />
          Origine e destinazione selezionate
      </div>
      <div className="assignx-form-grid">
        <label className="full">
          Nome
          <input value={draft.name || ''} disabled={busy} placeholder="Nuovo collegamento" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </label>
        <label className="full">
          Tipo strada
          <select value={draft.highway} disabled={busy} onChange={(e) => setDraft({ ...draft, highway: e.target.value, capacity: draft.capacityAuto === false ? draft.capacity : estimateCapacityVph(e.target.value, draft.lanes) })}>
            {Object.entries({ motorway: 'Autostrada', motorway_link: 'Svincolo autostradale', trunk: 'Extraurbana principale', trunk_link: 'Svincolo extraurbano', primary: 'Strada primaria', primary_link: 'Svincolo primario', secondary: 'Strada secondaria', secondary_link: 'Svincolo secondario', tertiary: 'Strada terziaria', tertiary_link: 'Svincolo terziario', unclassified: 'Non classificata', residential: 'Residenziale', living_street: 'Zona residenziale', service: 'Servizio' }).map(([h, label]) => (
              <option key={h} value={h}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          Corsie
          <input type="number" min={1} max={8} value={draft.lanes} disabled={busy} onChange={(e) => { const lanes = Number(e.target.value); setDraft({ ...draft, lanes, capacity: draft.capacityAuto === false ? draft.capacity : estimateCapacityVph(draft.highway, lanes) }); }} />
        </label>
        <label>
          Velocità (km/h)
          <input type="number" value={draft.speed} disabled={busy} onChange={(e) => setDraft({ ...draft, speed: Number(e.target.value) })} />
        </label>
        <label>
          Capacità (v/h)
          <input type="number" value={draft.capacity} disabled={busy} onChange={(e) => setDraft({ ...draft, capacity: Number(e.target.value), capacityAuto: false })} />
          {draft.capacityAuto !== false && <small>Stima automatica per categoria e corsie</small>}
        </label>
        <label>
          Fattore capacità
          <input type="number" step="0.1" value={draft.factor} disabled={busy} onChange={(e) => setDraft({ ...draft, factor: Number(e.target.value) })} />
        </label>
      </div>
      <div className="assignx-swrow">
        <span>
          Doppio senso
          <small>Crea anche l'arco nella direzione opposta</small>
        </span>
        <Switch checked={!!draft.twoWay} disabled={busy} onChange={(v) => setDraft({ ...draft, twoWay: v })} />
      </div>
      <div className="assignx-drawer-actions">
        <Button disabled={busy} onClick={onCancel}>Annulla</Button>
        <Button type="primary" loading={busy} disabled={busy} style={{ background: '#353A0A' }} onClick={onConfirm}>Conferma</Button>
      </div>
    </div>
  );
}

export function CompareBar({ left, right, options, onLeft, onRight, deltas, loading = false }) {
  return (
    <div className="assignx-compare-bar">
      <label className="assignx-compare-field">
        Sinistra
        <select value={left} disabled={loading} onChange={(e) => onLeft(e.target.value)}>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </label>
      <span className="assignx-compare-vs">vs</span>
      <label className="assignx-compare-field">
        Destra
        <select value={right} disabled={loading} onChange={(e) => onRight(e.target.value)}>
          {options.map((o) => <option key={`r-${o.id}`} value={o.id}>{o.name}</option>)}
        </select>
      </label>
      {deltas && (
        <div className="assignx-delta-cells">
          {Object.entries(deltas)
            .filter(([k]) => k !== 'pct_change' && DELTA_LABELS[k])
            .slice(0, 3)
            .map(([k, v]) => (
              <span key={k} className={`assignx-delta-chip ${Number(v) > 0 ? 'up' : 'down'}`}>
                <span className="lbl">{DELTA_LABELS[k]}</span>
                {Number(v) > 0 ? '+' : ''}{Number(v).toFixed(1)}
              </span>
            ))}
        </div>
      )}
    </div>
  );
}
